import { useCallback, useEffect, useState } from 'react';
import api from '../../lib/api';
import { useAuthStore } from '../../stores/authStore';
import Button from '../../components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import { MESSAGING_EVENTS } from '../../lib/messagingConstants';

type OutboxRow = {
  id: string;
  channel: string;
  status: string;
  messagingEventKey?: string | null;
  templateKey?: string | null;
  destination: string;
  attempts: number;
  lastError?: string | null;
  createdAt: string;
  organization?: { id: string; name: string } | null;
  recipientUser?: { id: string; fullName?: string | null } | null;
};

const STATUSES = ['', 'PENDING', 'PROCESSING', 'SENT', 'FAILED', 'CANCELLED'] as const;
const CHANNELS = ['', 'IN_APP', 'WHATSAPP', 'EMAIL', 'SMS'] as const;

function formatTs(iso: string) {
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

export default function MessagingLogPage() {
  const { user } = useAuthStore();
  const [rows, setRows] = useState<OutboxRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterChannel, setFilterChannel] = useState('');
  const [filterEvent, setFilterEvent] = useState('');
  const [errorSearch, setErrorSearch] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params: Record<string, string | number> = { limit: 200 };
      if (filterStatus) params.status = filterStatus;
      if (filterChannel) params.channel = filterChannel;
      if (filterEvent) params.messagingEventKey = filterEvent;
      if (errorSearch.trim()) params.errorSearch = errorSearch.trim();
      if (dateFrom) params.dateFrom = new Date(dateFrom).toISOString();
      if (dateTo) params.dateTo = new Date(dateTo).toISOString();
      const res = await api.get<OutboxRow[]>('/notification-outbox', { params });
      setRows(res.data);
    } catch (e: unknown) {
      setError(
        (e as { response?: { data?: { error?: string } } })?.response?.data?.error ||
          'Failed to load messaging log'
      );
    } finally {
      setLoading(false);
    }
  }, [filterStatus, filterChannel, filterEvent, errorSearch, dateFrom, dateTo]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="space-y-6 max-w-6xl">
      <div>
        <h1 className="text-[2.25rem] font-semibold tracking-tightest text-glass-text">Messaging log</h1>
        <p className="text-glass-text-2 text-sm mt-1">
          Delivery queue for in-app, WhatsApp, email, and SMS (SMS requires provider setup). Rows with
          FAILED and an error mentioning SMTP mean email is not configured on the server.
        </p>
      </div>

      <Card>
        <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2">
          <CardTitle>Filters</CardTitle>
          <Button size="sm" variant="outline" onClick={load} disabled={loading}>
            Refresh
          </Button>
        </CardHeader>
        <CardContent className="flex flex-wrap gap-3 items-end">
          <div>
            <label className="text-xs text-glass-text-2 block mb-1">Status</label>
            <Select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
              {STATUSES.map((s) => (
                <option key={s || 'all'} value={s}>
                  {s || 'All'}
                </option>
              ))}
            </Select>
          </div>
          <div>
            <label className="text-xs text-glass-text-2 block mb-1">Channel</label>
            <Select value={filterChannel} onChange={(e) => setFilterChannel(e.target.value)}>
              {CHANNELS.map((c) => (
                <option key={c || 'all'} value={c}>
                  {c || 'All'}
                </option>
              ))}
            </Select>
          </div>
          <div>
            <label className="text-xs text-glass-text-2 block mb-1">Event</label>
            <Select value={filterEvent} onChange={(e) => setFilterEvent(e.target.value)}>
              <option value="">All events</option>
              {MESSAGING_EVENTS.map((ev) => (
                <option key={ev.key} value={ev.key}>
                  {ev.label}
                </option>
              ))}
            </Select>
          </div>
          <div>
            <label className="text-xs text-glass-text-2 block mb-1">Error contains</label>
            <Input
              placeholder="SMTP…"
              value={errorSearch}
              onChange={(e) => setErrorSearch(e.target.value)}
              className="w-40"
            />
          </div>
          <div>
            <label className="text-xs text-glass-text-2 block mb-1">From</label>
            <Input type="datetime-local" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
          </div>
          <div>
            <label className="text-xs text-glass-text-2 block mb-1">To</label>
            <Input type="datetime-local" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
          </div>
          <Button type="button" size="sm" onClick={load}>
            Apply
          </Button>
        </CardContent>
      </Card>

      {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}

      <Card>
        <CardHeader>
          <CardTitle>Entries ({user?.role})</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-glass-text-2 text-sm">Loading…</p>
          ) : rows.length === 0 ? (
            <p className="text-glass-text-2 text-sm">No entries.</p>
          ) : (
            <div className="overflow-x-auto border border-glass rounded-xl">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-glass bg-glass-2 text-left text-glass-text-2">
                    <th className="p-2 font-medium">When</th>
                    <th className="p-2 font-medium">Status</th>
                    <th className="p-2 font-medium">Channel</th>
                    <th className="p-2 font-medium">Event</th>
                    <th className="p-2 font-medium">Destination</th>
                    <th className="p-2 font-medium">Org</th>
                    <th className="p-2 font-medium">Recipient</th>
                    <th className="p-2 font-medium">Error</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.id} className="border-b border-glass/60 align-top">
                      <td className="p-2 text-glass-text-2 whitespace-nowrap">{formatTs(r.createdAt)}</td>
                      <td className="p-2">{r.status}</td>
                      <td className="p-2">{r.channel}</td>
                      <td className="p-2 text-xs">{r.messagingEventKey || '—'}</td>
                      <td className="p-2 font-mono text-xs max-w-[8rem] break-all">{r.destination}</td>
                      <td className="p-2">{r.organization?.name || '—'}</td>
                      <td className="p-2">{r.recipientUser?.fullName || '—'}</td>
                      <td className="p-2 text-xs text-red-500/90 max-w-[14rem] break-words">
                        {r.lastError || '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
